import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Track } from 'livekit-client';
import { useTracks } from '@livekit/components-react';
import FloatingAnnotationToolbar from './FloatingAnnotationToolbar';
import useAnnotationSync from './useAnnotationSync';
import { ANNOTATION_COLORS } from './annotationProtocol';

function findScreenShareVideo(root) {
  if (!root) return null;
  const bySource = root.querySelector(
    'video[data-lk-source="screen_share"], video[data-lk-source="screenShare"]',
  );
  if (bySource) return bySource;

  const focused = root.querySelector(
    '.lk-focused-participant video, .lk-focus-layout .lk-participant-tile[data-lk-source="screen_share"] video',
  );
  if (focused) return focused;

  let best = null;
  let bestArea = 0;
  root.querySelectorAll('video').forEach((v) => {
    const area = (v.clientWidth || 0) * (v.clientHeight || 0);
    if (area > bestArea) {
      bestArea = area;
      best = v;
    }
  });
  return best;
}

/** Content box for object-fit contain/cover, or element box as fallback. */
function videoContentBox(video) {
  if (!video) return null;
  const br = video.getBoundingClientRect();
  if (!br.width || !br.height) return null;

  const vw = video.videoWidth || 0;
  const vh = video.videoHeight || 0;
  if (!vw || !vh) {
    return { left: br.left, top: br.top, width: br.width, height: br.height };
  }

  const fit = (getComputedStyle(video).objectFit || 'contain').toLowerCase();
  if (fit === 'cover') {
    return { left: br.left, top: br.top, width: br.width, height: br.height };
  }

  const videoAspect = vw / vh;
  const elAspect = br.width / br.height;
  let width;
  let height;
  let left;
  let top;
  if (elAspect > videoAspect) {
    height = br.height;
    width = height * videoAspect;
    left = br.left + (br.width - width) / 2;
    top = br.top;
  } else {
    width = br.width;
    height = width / videoAspect;
    left = br.left;
    top = br.top + (br.height - height) / 2;
  }
  return { left, top, width, height };
}

/**
 * Annotation layer over LiveKit screen share.
 * Canvas always mounts when a share exists so drawing never depends on fragile video queries.
 */
export default function ScreenShareAnnotationLayer() {
  const controller = useAnnotationSync();
  const screenShares = useTracks([Track.Source.ScreenShare], {
    onlySubscribed: false,
  });
  const hasShare = screenShares.some((t) => Boolean(t.publication));
  // Prefer local share identity, else first remote sharer (focused layout usually puts it first).
  const shareIdentity = (() => {
    const local = screenShares.find((t) => t.participant?.isLocal && t.publication);
    if (local?.participant?.identity) return local.participant.identity;
    const remote = screenShares.find((t) => t.publication && t.participant?.identity);
    return remote?.participant?.identity || null;
  })();

  const layerRef = useRef(null);
  const canvasRef = useRef(null);
  const drawRectRef = useRef(null); // {left, top, width, height} in viewport coords
  const [, setTick] = useState(0);
  const drawing = useRef(false);
  const rafPaint = useRef(0);

  useEffect(() => controller.subscribe(() => setTick((t) => t + 1)), [controller]);

  useEffect(() => {
    if (!hasShare) {
      controller.setShareTarget(null);
      controller.clearAll({ broadcast: false });
      return;
    }
    // Every participant viewing this share can draw; strokes sync to the room.
    controller.setShareTarget(shareIdentity);
    controller.setEnabled(true);
  }, [hasShare, shareIdentity, controller]);

  const resolveDrawRect = useCallback(() => {
    const layer = layerRef.current;
    if (!layer) return null;
    const layerBounds = layer.getBoundingClientRect();
    if (!layerBounds.width || !layerBounds.height) return null;

    const root = layer.parentElement;
    const video = findScreenShareVideo(root);
    const box = videoContentBox(video);

    // Prefer the real screen-share video content box (object-fit: contain).
    // Avoid oversized fallbacks — wrong rects cause 2x scale / wrong position.
    if (!box || box.width < 8 || box.height < 8) {
      drawRectRef.current = null;
      return null;
    }

    drawRectRef.current = box;
    return { layerBounds, rect: box };
  }, []);

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    const layer = layerRef.current;
    if (!canvas || !layer) return;

    const resolved = resolveDrawRect();
    if (!resolved) return;
    const { rect } = resolved;
    const canvasBounds = canvas.getBoundingClientRect();

    const w = Math.max(1, Math.round(canvasBounds.width));
    const h = Math.max(1, Math.round(canvasBounds.height));
    const dpr = window.devicePixelRatio || 1;

    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const local = {
      x: rect.left - canvasBounds.left,
      y: rect.top - canvasBounds.top,
      w: rect.width,
      h: rect.height,
    };

    for (const stroke of controller.strokesForPaint) {
      if (!stroke.points.length) continue;
      ctx.beginPath();
      ctx.strokeStyle = ANNOTATION_COLORS[stroke.color] || ANNOTATION_COLORS.red;
      // Stable brush size in CSS pixels (avoids 2x thickness on large shares).
      ctx.lineWidth = stroke.width || 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const first = stroke.points[0];
      ctx.moveTo(local.x + first.x * local.w, local.y + first.y * local.h);
      for (let i = 1; i < stroke.points.length; i += 1) {
        const p = stroke.points[i];
        ctx.lineTo(local.x + p.x * local.w, local.y + p.y * local.h);
      }
      ctx.stroke();
    }
  }, [controller, resolveDrawRect]);

  const schedulePaint = useCallback(() => {
    if (rafPaint.current) return;
    rafPaint.current = window.requestAnimationFrame(() => {
      rafPaint.current = 0;
      paint();
    });
  }, [paint]);

  useEffect(() => {
    schedulePaint();
  }, [schedulePaint, controller.version]);

  useEffect(() => {
    if (!hasShare) return undefined;
    const layer = layerRef.current;
    if (!layer) return undefined;
    const onResize = () => schedulePaint();
    const ro = new ResizeObserver(onResize);
    ro.observe(layer);
    window.addEventListener('resize', onResize);
    const id = window.setInterval(onResize, 400);
    // First paint after mount
    schedulePaint();
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
      window.clearInterval(id);
      if (rafPaint.current) window.cancelAnimationFrame(rafPaint.current);
    };
  }, [hasShare, schedulePaint]);

  const pointFromEvent = (e) => {
    let rect = drawRectRef.current;
    if (!rect) {
      const resolved = resolveDrawRect();
      rect = resolved?.rect;
    }
    if (!rect || !rect.width || !rect.height) return null;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    // Soft clamp — still allow drawing near edges
    if (x < -0.05 || x > 1.05 || y < -0.05 || y > 1.05) return null;
    return {
      x: Math.min(1, Math.max(0, x)),
      y: Math.min(1, Math.max(0, y)),
    };
  };

  const onPointerDown = (e) => {
    if (!controller.enabled || !controller.shareTarget) return;
    if (e.target?.closest?.('[data-annotation-toolbar]')) return;
    const p = pointFromEvent(e);
    if (!p) return;
    drawing.current = true;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    controller.beginStroke(p);
    schedulePaint();
  };

  const onPointerMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pointFromEvent(e);
    if (p) {
      controller.appendPoint(p);
      schedulePaint();
    }
  };

  const onPointerUp = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    controller.endStroke();
    schedulePaint();
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
  };

  if (!hasShare) return null;

  return (
    <div
      ref={layerRef}
      className="absolute inset-0 z-[35]"
      style={{ pointerEvents: 'none' }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{
          width: '100%',
          height: '100%',
          pointerEvents: controller.enabled ? 'auto' : 'none',
          touchAction: 'none',
          cursor: controller.enabled
            ? (controller.tool === 'eraser' ? 'cell' : 'crosshair')
            : 'default',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
      <div data-annotation-toolbar style={{ pointerEvents: 'auto' }}>
        <FloatingAnnotationToolbar controller={controller} />
      </div>
    </div>
  );
}
